import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MibDatepickerComponent,
  MibFilterChip,
  MibFilterChipsComponent,
  MibIconComponent,
  MibListItemDirective,
} from '@medinbox/angular';
import { CASE_CATEGORY_CHIPS } from '../case-categories';
import { CasePeriod, CasePublication } from '@shared/enums/case-library';
import { CASE_SORT_ORDERS, CASE_SORTS, CaseLibraryStore } from '../case-library-store';

let nextId = 0;

const PERIOD_CHIPS: MibFilterChip[] = [
  { id: CasePeriod.All, label: 'All', all: true },
  { id: CasePeriod.Week, label: 'This week' },
  { id: CasePeriod.Month, label: 'This month' },
  { id: CasePeriod.Custom, label: 'Custom' },
];

const PUBLICATION_CHIPS: MibFilterChip[] = [
  { id: CasePublication.All, label: 'All', all: true },
  { id: CasePublication.Published, label: 'Published' },
  { id: CasePublication.Unpublished, label: 'Not published' },
];

/** The chip row presses All for an empty selection. */
function rowOf(choice: string, all: string): string[] {
  return choice === all ? [] : [choice];
}

const FILTER_HEADING_CLASSES = 'text-xs font-semibold text-gray-500';

/**
 * What the library can be narrowed by, beside its cases. Every choice is the library's own, so the
 * chips above the cases and this panel can never disagree.
 */
@Component({
  selector: 'mai-case-filter-panel',
  imports: [
    MibDatepickerComponent,
    MibFilterChipsComponent,
    MibIconComponent,
    MibListItemDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './case-filter-panel.html',
})
export class CaseFilterPanel {
  protected readonly library = inject(CaseLibraryStore);
  protected readonly uid = `mai-case-filter-${nextId++}`;
  private readonly sortOptions = viewChildren<ElementRef<HTMLButtonElement>>('sortOption');

  /** Shows the sort choices, for the layouts whose toolbar has no sort menu. */
  readonly sortable = input(false);

  protected readonly sortOrders = CASE_SORT_ORDERS;
  protected readonly sorts = CASE_SORTS;

  protected readonly categoryChips = CASE_CATEGORY_CHIPS;

  protected readonly headingClasses = FILTER_HEADING_CLASSES;
  protected readonly periodChips = PERIOD_CHIPS;
  protected readonly publicationChips = PUBLICATION_CHIPS;
  protected readonly periodRow = computed(() => rowOf(this.library.period(), CasePeriod.All));
  protected readonly publicationRow = computed(() =>
    rowOf(this.library.publication(), CasePublication.All),
  );

  protected readonly from = new FormControl<string | null>(null);
  protected readonly to = new FormControl<string | null>(null);

  constructor() {
    // Written without an event: only the reader's own edits make the period custom.
    effect(() => {
      const { start, end } = this.library.range();
      this.from.setValue(start, { emitEvent: false });
      this.to.setValue(end, { emitEvent: false });
    });
    this.from.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((start) => this.library.setRange({ start, end: this.to.value }));
    this.to.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((end) => this.library.setRange({ start: this.from.value, end }));
  }

  /* Custom keeps the dates on screen and lets the reader change them: only All and the presets fill them. */
  protected choosePeriod([chosen]: string[]): void {
    const period = (chosen ?? CasePeriod.All) as CasePeriod;
    if (period === CasePeriod.Custom) {
      this.library.period.set(CasePeriod.Custom);
    } else {
      this.library.choosePeriod(period);
    }
  }

  focusSort(): void {
    const selected = this.sortOrders.indexOf(this.library.sortOrder());
    this.sortOptions()[selected]?.nativeElement.focus();
  }

  protected choosePublication([chosen]: string[]): void {
    this.library.publication.set((chosen ?? CasePublication.All) as CasePublication);
  }
}
